import React from "react";
import { Box, Typography, useTheme, useMediaQuery } from "@mui/material";
import {
  IMG_ORGANIC_COTTON_IMAGE as OrganicCottonImage,
  IMG_90_LESS_WATER as LessWater,
  IMG_50_LESS_CO2_EM as LessCO2,
  IMG_60_LESS_ENERGY as LessEnergy,
  IMG_GOTS_HANG_TAG as GotsHangTag,
} from "./imageConfig";

const Resources = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        minHeight: "100vh",
        p: isMobile ? 1 : 2,
        mt: isMobile ? 12 : 16,
      }}
    >
      {/* Image Container */}
      <Box
        sx={{
          position: "relative",
          display: "flex",
          justifyContent: "center",
          overflow: "hidden",
          lineHeight: 0,
          width: "100%",
          mt: isMobile ? 2 : 4,
        }}
      >
        <Box
          component="img"
          src={OrganicCottonImage}
          alt="Organic Cotton"
          sx={{
            display: "block",
            width: isMobile ? "100%" : "60%",
            height: "auto",
          }}
        />

        {/* "Our RESOURCES" Text */}
        <Typography
          variant="h2"
          sx={{
            position: "absolute",
            top: isMobile ? 10 : 0,
            left: isMobile ? 10 : 40,
            fontFamily: '"Helvetica Neue", Arial, sans-serif',
            fontSize: isMobile ? "2.2rem" : isTablet ? "3.5rem" : "5rem",
            fontWeight: 400,
            letterSpacing: "0.1em",
            color: "#000",
            zIndex: 2,
            textAlign: "left",
          }}
        >
          Our
          <br />
          RESOURCES
        </Typography>
      </Box>

      {/* Additional Content - Centered */}
      <Box
        sx={{
          maxWidth: "800px",
          textAlign: "center",
          mt: isMobile ? 4 : 8,
          mb: isMobile ? 4 : 8,
          px: isMobile ? 1 : 2,
        }}
      >
        <Typography
          variant="body1"
          sx={{
            fontSize: isMobile ? "1rem" : isTablet ? "1.2rem" : "1.5rem",
            lineHeight: 1.6,
            mb: isMobile ? 2 : 4,
            color: "rgba(0, 0, 0, 0.8)",
          }}
        >
          We only produce our clothes in small quantities and limited editions
          to avoid overproduction.
        </Typography>

        <Typography
          variant="body1"
          sx={{
            fontSize: isMobile ? "1rem" : isTablet ? "1.2rem" : "1.5rem",
            lineHeight: 1.6,
            mb: isMobile ? 2 : 4,
            color: "rgba(0, 0, 0, 0.8)",
          }}
        >
          At DAINASOUL, we rely on sustainable and certified materials
          such as organic cotton fibers.
        </Typography>

        <Typography
          variant="body1"
          sx={{
            fontSize: isMobile ? "1rem" : isTablet ? "1.2rem" : "1.5rem",
            lineHeight: 1.6,
            color: "rgba(0, 0, 0, 0.8)",
          }}
        >
          Unlike conventional cotton cultivation, the use of synthetic chemical
          pesticides and fertilisers is prohibited in organic cotton
          cultivation. The plants are grown in rotation with other crops to
          maintain the organic soil quality. The cultivation of organic cotton
          can save up to 90%* water and 60%* energy compared to conventional
          cotton, and CO2 emissions can be reduced by up to half.
        </Typography>
      </Box>

      {/* Triangle Layout Images */}
      <Box
        sx={{
          position: "relative",
          width: "100%",
          maxWidth: isMobile ? "100%" : "730px",
          height: isMobile ? "auto" : "580px",
          mt: isMobile ? 4 : 2,
          mb: isMobile ? 4 : 8,
          minHeight: isMobile ? "300px" : "auto",
        }}
      >
        {/* Mobile layout - stacked images */}
        {isMobile ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Box
              component="img"
              src={LessWater}
              alt="90% Less Water"
              sx={{
                width: "80%",
                height: "auto",
                borderRadius: 2,
              }}
            />
            <Box
              component="img"
              src={LessCO2}
              alt="50% Less CO2"
              sx={{
                width: "80%",
                height: "auto",
                borderRadius: 2,
              }}
            />
            <Box
              component="img"
              src={LessEnergy}
              alt="60% Less Energy"
              sx={{
                width: "80%",
                height: "auto",
                borderRadius: 2,
              }}
            />
          </Box>
        ) : (
          <>
            {/* Desktop/Tablet layout - triangle */}
            {/* Top Center Image - LessWater */}
            <Box
              component="img"
              src={LessWater}
              alt="90% Less Water"
              sx={{
                position: "absolute",
                top: 0,
                left: "50%",
                transform: "translateX(-50%)",
                width: isTablet ? "240px" : "289px",
                height: "auto",
                borderRadius: 2,
              }}
            />

            {/* Bottom Left Image - LessCO2 */}
            <Box
              component="img"
              src={LessCO2}
              alt="50% Less CO2"
              sx={{
                position: "absolute",
                bottom: 0,
                left: 0,
                width: isTablet ? "240px" : "289px",
                height: "auto",
                borderRadius: 2,
              }}
            />

            {/* Bottom Right Image - LessEnergy */}
            <Box
              component="img"
              src={LessEnergy}
              alt="60% Less Energy"
              sx={{
                position: "absolute",
                bottom: 0,
                right: 0,
                width: isTablet ? "240px" : "289px",
                height: "auto",
                borderRadius: 2,
              }}
            />
          </>
        )}
      </Box>

      {/* GOTS Certification Section */}
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          alignItems: "flex-start",
          justifyContent: "center",
          gap: isMobile ? 2 : 4,
          maxWidth: "1200px",
          mx: "auto",
          textAlign: isMobile ? "center" : "left",
          mt: 4,
          width: "100%",
        }}
      >
        {/* Left Side Image */}
        <Box
          component="img"
          src={GotsHangTag}
          alt="GOTS Certification"
          sx={{
            width: isMobile ? "100%" : isTablet ? "45%" : "40%",
            height: "auto",
            borderRadius: 2,
            boxShadow: 2,
            mb: isMobile ? 2 : 0,
          }}
        />

        {/* Right Side Content */}
        <Box sx={{ width: isMobile ? "100%" : isTablet ? "55%" : "60%" }}>
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 400, 
              mb: 2,
              fontSize: isMobile ? "1.5rem" : isTablet ? "1.8rem" : "2.125rem",
              textAlign: isMobile ? "center" : "left"
            }}
          >
            Global Organic Textile Standard (GOTS)
          </Typography>

          <Typography 
            sx={{ 
              mb: 2, 
              fontSize: isMobile ? "1rem" : isTablet ? "1.1rem" : "1.25rem", 
              color: "#333" 
            }}
          >
            Our products are crafted with primarily eco-friendly,
            high-performance fabrics. Since summer 2024, we have been producing
            selected designs according to the Global Organic Textile Standard
            (GOTS), the world's leading textile processing standard for organic
            fibres. This standard incorporates ecological and social criteria in
            the production of textiles. The entire supply chain of the certified
            products is subject to an independent and transparent certification
            process.
          </Typography>

          <Typography 
            sx={{ 
              mb: 2, 
              fontSize: isMobile ? "1rem" : isTablet ? "1.1rem" : "1.25rem", 
              color: "#333" 
            }}
          >
            In addition, we are working on having all products certified by GOTS
            in the future. In this way, we ensure that the entire production
            chain, from cultivation to the finished product and its
            distribution, fulfils prescribed international environmental and
            social criteria.
          </Typography>

          <Typography 
            sx={{ 
              fontWeight: 400, 
              mt: isMobile ? 2 : 4, 
              mb: 1, 
              fontSize: isMobile ? "1rem" : isTablet ? "1.1rem" : "1.25rem",
              textAlign: isMobile ? "center" : "left"
            }}
          >
            Focus points:
          </Typography>

          <Box
            component="ul"
            sx={{
              paddingLeft: isMobile ? "1.5rem" : "1rem",
              marginTop: 0,
              color: "#333",
              fontSize: isMobile ? "1rem" : isTablet ? "1.1rem" : "1.25rem",
              textAlign: "left",
            }}
          >
            <li>Environmentally friendly production and processing methods</li>
            <li>Compliance with and improvement of working conditions</li>
            <li>Promotion of the use of organically grown fibres</li>
            <li>
              Prohibition of hazardous additives such as heavy metals, aromatic
              solvents, etc.
            </li>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Resources;
